import {
  AddressLookup,
  Button,
  type LoqateAddressDetailsItem,
  LoqateAddressLookupService,
} from "@simplybusiness/mobius";
import { useRef } from "react";
import { useSnowplowContext } from "@simplybusiness/services";
import type { QuestionComponentProps } from "../../types";
import type { FocusEvent } from "react";
import { transformAddressData } from "./transformAddressData";
import { useQuestionRefs } from "../../../../../../../hooks/useQuestionRefs";
import { useQuestionnaire } from "../../../../../../../features/questionnaire";

export type MappedFields = {
  id: string;
  required: boolean;
  associatedQuestionId: string;
};

type AddressLookupQuestionConfig = {
  apiKey: string;
  mappedFields: MappedFields[];
};

export const AddressLookupQuestion = ({
  questionFieldProps,
  config,
  sectionTitle,
  setAddressMode,
}: QuestionComponentProps) => {
  const {
    value,
    onChange,
    onFocus,
    questionLabelProps,
    helpInfo,
    ...otherProps
  } = questionFieldProps;
  const typedConfig = config as AddressLookupQuestionConfig;
  const { apiKey, mappedFields } = typedConfig;
  const { snowplow } = useSnowplowContext();
  const { name } = questionFieldProps;
  const sendAddressLookupEvent = useRef<boolean>(false);
  const { focusQuestion } = useQuestionRefs();
  const { questions } = useQuestionnaire();

  const handleFocus = (event: FocusEvent) => {
    snowplow?.trigger("addressLookupFocused", {
      category: name,
      label: "Address lookup focused",
    });

    onFocus?.(event);
  };

  const handleOptionsDiplayed = () => {
    snowplow?.trigger("addressOptionsDisplayed", {
      category: name,
      label: "Address options displayed",
    });
  };

  const handleChange = (data: LoqateAddressDetailsItem) => {
    const transformedData = transformAddressData({
      questionName: name,
      addressData: data,
      mappedFields,
    });

    onChange?.({
      target: { name, value: transformedData },
    });
  };
  const showManualFields = () => {
    snowplow?.trigger("manualAddressClicked", {
      category:
        sectionTitle || questionFieldProps.label || questionFieldProps.name,
    });

    setAddressMode?.(name, "manual");

    // Find the first manual field to focus on
    const firstMappedField = mappedFields?.[0];
    if (firstMappedField?.associatedQuestionId) {
      // Delay focus until after the manual fields have mounted
      setTimeout(() => {
        const questionToFocus = questions.filter.getById(
          firstMappedField.associatedQuestionId,
        );

        if (questionToFocus?.name) {
          focusQuestion(questionToFocus.name);
        }
      }, 0);
    }
  };

  const handleAddressInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (value.length === 1 && !sendAddressLookupEvent?.current) {
      snowplow?.trigger("addressLookupTypedIn", {
        category: e.target.name,
        label: "First character input",
      });
      sendAddressLookupEvent.current = true;
    }
  };

  // Define the lookup service
  const loquateService: LoqateAddressLookupService =
    new LoqateAddressLookupService({ apiKey, countries: ["GB"] });

  return (
    <div className="mobius-address-lookup__container">
      <AddressLookup
        addressLookupService={loquateService}
        {...otherProps}
        onChange={handleAddressInput}
        value={value as string}
        key={value as string}
        placeholder="Enter your postcode"
        onAddressSelected={handleChange}
        onOptionsDisplayed={handleOptionsDiplayed}
        onFocus={handleFocus}
        minSearchLength={5}
      />
      <Button size="sm" variant="link" onClick={showManualFields}>
        Enter address manually
      </Button>
    </div>
  );
};
